/**
 * JOProductBadgesInjector — Renders up to 4 contextual badges on every product
 * (vivace, substrat, décoration minérale, aménagement…).
 *
 * Data source: `product.product_badges = [{key, label, value}, …]`
 * (seeded via backend/seed_product_badges.py — all 34 products).
 *
 * Icon mapping (fallback: Package):
 *   exposition   → Sun
 *   arrosage     → Droplet
 *   floraison    → Flower2
 *   rusticite    → Thermometer
 *   poids        → Package
 *   granulometrie/calibre → Ruler
 *   usage        → Sparkles
 *   couverture   → Grid3x3
 *   type         → Layers
 *   ph           → FlaskConical
 *
 * Injection points:
 *   - Shop cards: compact row inside each `[data-testid^="shop-product-"]`
 *   - Product detail: full row under the price area
 *
 * NON-CORE — additive DOM injector using createPortal into placeholder anchors.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Sun, Droplet, Flower2, Thermometer,
  Package, Ruler, Sparkles, Grid3x3, Layers, FlaskConical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARD_ANCHOR_CLASS = "jo-product-badges-card";
const DETAIL_ANCHOR_ID = "jo-product-badges-detail";

const ICONS = {
  exposition: Sun,
  arrosage: Droplet,
  floraison: Flower2,
  rusticite: Thermometer,
  poids: Package,
  granulometrie: Ruler,
  calibre: Ruler,
  usage: Sparkles,
  couverture: Grid3x3,
  type: Layers,
  ph: FlaskConical,
};

function isShopRoute(pathname) {
  return pathname === "/shop" || pathname.startsWith("/shop");
}

function BadgePill({ badge, testid }) {
  const Icon = ICONS[badge.key] || Package;
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 border border-neutral-200 shadow-sm"
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5 text-[color:var(--brand-navy,#0e2a4d)] shrink-0" strokeWidth={2.2} />
      <div className="leading-tight">
        <div className="text-[9px] uppercase tracking-wider font-semibold text-neutral-500">
          {badge.label}
        </div>
        <div className="text-[11px] font-bold text-neutral-900 whitespace-nowrap">
          {badge.value}
        </div>
      </div>
    </div>
  );
}

function BadgesRow({ badges, compact = false }) {
  if (!Array.isArray(badges) || badges.length === 0) return null;
  const cls = compact
    ? "flex flex-wrap gap-1 px-2 pb-2"
    : "flex flex-wrap gap-2 mt-4";
  return (
    <div className={cls} data-testid="jo-product-badges">
      {badges.map((b, i) => (
        <BadgePill key={`${b.key}-${i}`} badge={b} testid={`badge-${b.key}`} />
      ))}
    </div>
  );
}

function extractDetailSlug(pathname) {
  const m = pathname.match(/^\/shop\/([^/?#]+)$/);
  return m ? m[1] : null;
}

function ensureCardAnchors(productsBySlug) {
  const anchors = new Map();
  document.querySelectorAll('[data-testid^="shop-product-"]').forEach((card) => {
    const slug = (card.getAttribute("data-testid") || "").replace(/^shop-product-/, "");
    const p = productsBySlug.get(slug);
    const badges = Array.isArray(p?.product_badges) ? p.product_badges : null;
    if (!badges || badges.length === 0) return;
    let anchor = card.querySelector(`.${CARD_ANCHOR_CLASS}`);
    if (!anchor) {
      anchor = document.createElement("div");
      anchor.className = CARD_ANCHOR_CLASS;
      card.appendChild(anchor);
    }
    anchors.set(slug, { anchor, badges });
  });
  return anchors;
}

function ensureDetailAnchor() {
  let anchor = document.getElementById(DETAIL_ANCHOR_ID);
  if (anchor?.isConnected) return anchor;
  const media = document.querySelector('[data-testid="product-media"]');
  if (!media) return null;
  const parent = media.parentElement;
  if (!parent) return null;
  const right = parent.children[1];
  if (!right) return null;
  anchor = document.createElement("div");
  anchor.id = DETAIL_ANCHOR_ID;
  right.appendChild(anchor);
  return anchor;
}

function removeDetailAnchor() {
  const el = document.getElementById(DETAIL_ANCHOR_ID);
  if (el) el.remove();
}

export default function JOProductBadgesInjector() {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isShopRoute(location.pathname)) return;
    let alive = true;
    axios.get(`${API}/shop/products`).then((r) => {
      if (alive) setProducts(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [location.pathname]);

  useEffect(() => {
    if (!isShopRoute(location.pathname)) return;
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      setTick((t) => t + 1);
      if (tries > 20) clearInterval(interval);
    }, 350);
    const observer = new MutationObserver(() => setTick((t) => t + 1));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isShopRoute(location.pathname)) {
      document.querySelectorAll(`.${CARD_ANCHOR_CLASS}`).forEach((el) => el.remove());
      removeDetailAnchor();
    }
  }, [location.pathname]);

  const productsBySlug = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p.slug, p);
    return m;
  }, [products]);

  const detailSlug = extractDetailSlug(location.pathname);
  const detailProduct = detailSlug ? productsBySlug.get(detailSlug) : null;

  const cardAnchors = useMemo(() => {
    if (products.length === 0) return new Map();
    return ensureCardAnchors(productsBySlug);
  }, [products, productsBySlug, tick]);

  const detailAnchor = useMemo(() => {
    const badges = detailProduct?.product_badges;
    if (!Array.isArray(badges) || badges.length === 0) {
      removeDetailAnchor();
      return null;
    }
    return ensureDetailAnchor();
  }, [detailProduct, tick]);

  return (
    <>
      {[...cardAnchors.entries()].map(([slug, { anchor, badges }]) => (
        createPortal(<BadgesRow key={slug} badges={badges} compact />, anchor)
      ))}
      {detailAnchor && detailProduct?.product_badges
        ? createPortal(<BadgesRow badges={detailProduct.product_badges} />, detailAnchor)
        : null}
    </>
  );
}
